import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Popover, Space } from 'antd';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';

export interface ConfirmProps {
    /** Confirmation title */
    title: ReactNode;
    /** Optional description below the title */
    description?: ReactNode;
    /** OK button text */
    okText?: string;
    /** Cancel button text */
    cancelText?: string;
    /** Use danger styling for OK button */
    okDanger?: boolean;
    /** Handler called when OK is clicked. Can be async. */
    onOk?: () => void | Promise<void>;
    /** Handler called when Cancel is clicked */
    onCancel?: () => void;
    /** Notifies the trigger when the confirmation opens or closes. */
    onOpenChange?: (open: boolean) => void;
    /** Custom icon (defaults to QuestionCircleOutlined) */
    icon?: ReactNode;
    /** Whether the confirm is disabled */
    disabled?: boolean;
    /** The trigger element */
    children: ReactNode;
    /** Popover placement */
    placement?:
        | 'top'
        | 'left'
        | 'right'
        | 'bottom'
        | 'topLeft'
        | 'topRight'
        | 'bottomLeft'
        | 'bottomRight'
        | 'leftTop'
        | 'leftBottom'
        | 'rightTop'
        | 'rightBottom';
    /** Whether to stop click propagation when popup is clicked */
    stopPropagation?: boolean;
}

/**
 * Confirmation popover with ButtonModern buttons.
 * Drop-in replacement for Antd Popconfirm with custom button styling.
 *
 * @example
 * <Confirm
 *   title="Delete this item?"
 *   description="This action cannot be undone."
 *   onOk={handleDelete}
 *   okText="Delete"
 *   okDanger
 * >
 *   <Button.Icon icon={<Trash2 />} color="red" />
 * </Confirm>
 *
 * @example
 * <Confirm
 *   title="Are you sure?"
 *   onOk={handleConfirm}
 * >
 *   <Button type="primary">Proceed</Button>
 * </Confirm>
 */
export function Confirm({
    title,
    description,
    okText = 'OK',
    cancelText = 'Cancel',
    okDanger = false,
    onOk,
    onCancel,
    onOpenChange,
    icon,
    disabled = false,
    children,
    placement = 'top',
    stopPropagation = true,
}: ConfirmProps) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const pending = useRef(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLElement | null>(null);
    const titleId = useId();
    const descriptionId = useId();

    const close = () => {
        // Do not steal focus from an error dialog opened by onOk.
        if (contentRef.current?.contains(document.activeElement)) triggerRef.current?.focus();
        updateOpen(false);
    };

    useEffect(() => {
        if (disabled && open) {
            setOpen(false);
            onOpenChange?.(false);
        }
    }, [disabled, open, onOpenChange]);

    const updateOpen = (newOpen: boolean) => {
        setOpen(newOpen);
        onOpenChange?.(newOpen);
    };

    const handleOpenChange = (newOpen: boolean) => {
        if ((disabled && newOpen) || pending.current) return;
        updateOpen(newOpen);
    };

    const handleOk = async (e: React.MouseEvent) => {
        if (stopPropagation) {
            e.stopPropagation();
        }
        if (disabled || pending.current) return;
        if (onOk) {
            pending.current = true;
            setLoading(true);
            try {
                await onOk();
            } finally {
                pending.current = false;
                setLoading(false);
                close();
            }
        } else {
            close();
        }
    };

    const handleCancel = (e: React.MouseEvent) => {
        if (stopPropagation) {
            e.stopPropagation();
        }
        if (pending.current) return;
        onCancel?.();
        close();
    };

    const handlePopupClick = (e: React.MouseEvent) => {
        if (stopPropagation) {
            e.stopPropagation();
        }
    };

    const content = (
        <div
            ref={contentRef}
            role="alertdialog"
            aria-labelledby={titleId}
            aria-describedby={description ? descriptionId : undefined}
            onClick={handlePopupClick}
            onKeyDown={(event) => {
                if (event.key !== 'Escape') return;
                // Escape dismisses this question first; a later Escape can ask to discard
                // the surrounding form. Neither surface closes during a pending action.
                event.stopPropagation();
                event.preventDefault();
                if (!pending.current) {
                    onCancel?.();
                    close();
                }
            }}
            className="max-w-64"
        >
            <div className="mb-2 flex items-center gap-2">
                <span className="flex-shrink-0 text-warning-foreground">{icon ?? <QuestionCircleOutlined />}</span>
                <div>
                    <div id={titleId} className="font-medium text-foreground">
                        {title}
                    </div>
                    {description && (
                        <div id={descriptionId} className="mt-0.5 text-muted-foreground text-sm">
                            {description}
                        </div>
                    )}
                </div>
            </div>
            <Space className="flex justify-end" size="small">
                <Button type="secondary" size="small" onClick={handleCancel} disabled={loading}>
                    {cancelText}
                </Button>
                <Button
                    type={okDanger ? 'danger-filled' : 'primary'}
                    size="small"
                    onClick={handleOk}
                    loading={loading}
                    disabled={loading || disabled}
                >
                    {okText}
                </Button>
            </Space>
        </div>
    );

    return (
        <Popover
            content={content}
            fresh
            trigger="click"
            open={open && !disabled}
            onOpenChange={handleOpenChange}
            placement={placement}
            arrow={{ pointAtCenter: true }}
            // Modal's dialog shell has pointer-events: none; its portalled child must opt in.
            styles={{ root: { pointerEvents: 'auto' } }}
            getPopupContainer={(trigger) => {
                triggerRef.current = trigger;
                // A body portal sits outside aria-modal and is hidden from assistive tech.
                return trigger.closest<HTMLElement>('[role="dialog"], [role="alertdialog"]') ?? document.body;
            }}
            afterOpenChange={(visible) => {
                if (visible) contentRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
            }}
        >
            {children}
        </Popover>
    );
}
